import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Link, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faDashboard, faPuzzlePiece, faShieldAlt, faBullhorn, faUsers,
  faChevronDown, faChevronRight, faAngleDoubleRight, faAngleDoubleLeft, faCog,
} from '@fortawesome/free-solid-svg-icons';
import logo from '../../assets/images/logo.png';

const links = [
  {
    to: '/private',
    icon: faDashboard,
    text: 'Dashboard',
    description: 'Overview and analytics',
  },
  {
    to: '/private/safety-logs/all',
    icon: faShieldAlt,
    text: 'Safety Assurance',
    description: 'Safety logs and compliance',
    submenuItems: [
      {
        to: '/private/safety-logs/all',
        text: 'Safety Logs',
        description: 'View and manage safety logs',
      },
    ],
  },
  {
    to: '/private/occurrences/all',
    icon: faPuzzlePiece,
    text: 'Safety Risk Management',
    description: 'Risk assessment and mitigation',
    submenuItems: [
      {
        to: '/private/occurrences/all',
        text: 'Occurrences',
        description: 'Track safety occurrences',
      },
    ],
  },
  {
    icon: faBullhorn,
    text: 'Safety Promotion',
    description: 'Communication, awareness and training',
    submenuItems: [
      {
        to: '/private/articles',
        text: 'Safety Articles',
        description: 'Educational content',
      },
      {
        to: '/private/publications',
        text: 'SMS Publications',
        description: 'Official documents',
      },
      {
        to: '/private/lessons',
        text: 'Lessons Learnt',
        description: 'Safety lessons and insights',
      },
      {
        to: '/private/newsletter/get-all-newsletters',
        text: 'News Letters',
        description: 'Newsletter management',
      },
      {
        to: '/private/ocms-redirect',
        text: 'Online Courses Backend',
        description: 'Manage courses, lessons and assessments',
      },
    ],
  },
  {
    icon: faUsers,
    text: 'User Management',
    description: 'User administration',
    submenuItems: [
      {
        to: '/private/users',
        text: 'All Users',
        description: 'Manage system users',
      },
    ],
  },
];

const SideMenu = ({ isMinimized, setIsMinimized, isAdmin }) => {
  const [showSubMenu, setShowSubMenu] = useState(null);
  const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0 });
  const location = useLocation();

  // Stable non-admin links (avoid recreating object each render to keep submenu toggle state)
  const nonAdminLinks = useMemo(() => ([
    links[0],
    {
      ...links[3],
      submenuItems: [
        {
          to: '/private/ocms-redirect',
          text: 'Online Courses',
          description: 'View available courses and assessments',
        },
      ],
    },
  ]), []);

  const toggleSubMenu = (link, e) => {
    if (isMinimized) {
      const rect = e.currentTarget.getBoundingClientRect();
      setPopoverPosition({ top: rect.top + window.scrollY, left: rect.right });
      setShowSubMenu(showSubMenu === link ? null : link);
    } else {
      setShowSubMenu(showSubMenu === link ? null : link);
    }
  };

  const isActive = (path) => location.pathname === path;

  const isSubMenuActive = (submenuItems) => submenuItems?.some(
    (item) => location.pathname === item.to,
  );

  const currentYear = new Date().getFullYear();

  return (
    <div
      className={`h-full flex flex-col bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900 min-h-screen transition-all duration-500 ease-in-out shadow-2xl ${
        isMinimized ? 'w-16' : 'w-72'
      }`}
    >
      {/* Header with Toggle */}
      <div className="flex items-center justify-between p-4 bg-gradient-to-r from-blue-600 to-purple-600 shadow-lg">
        <button
          type="button"
          onClick={() => setIsMinimized((prev) => !prev)}
          className="text-white hover:text-blue-200 p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-all duration-300 backdrop-blur-sm"
          aria-label={isMinimized ? 'Expand sidebar' : 'Minimize sidebar'}
        >
          <FontAwesomeIcon
            icon={isMinimized ? faAngleDoubleRight : faAngleDoubleLeft}
            className="text-lg transition-transform duration-300 hover:scale-110"
          />
        </button>
        {!isMinimized && (
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-white/10 rounded-xl backdrop-blur-sm">
              <img className="h-6 w-auto" src={logo} alt="Logo" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-white">ANS-SMS</h1>
              <p className="text-xs text-blue-100">Safety Management</p>
            </div>
          </div>
        )}
      </div>

      {/* Navigation Menu */}
      <nav
        className={`flex-1 px-4 py-6 space-y-3 bg-gradient-to-b from-gray-900/50 to-gray-800/50 backdrop-blur-sm ${
          isMinimized ? 'px-2' : 'px-6'
        }`}
      >
        {(isAdmin ? links : nonAdminLinks).map((link) => {
          const hasSubmenu = link.submenuItems && link.submenuItems.length > 0;
          const isCurrentActive = link.to ? isActive(link.to) : isSubMenuActive(link.submenuItems);
          const isSubmenuOpen = showSubMenu === link;

          return (
            <div key={link.text} className="space-y-2">
              {hasSubmenu ? (
                <button
                  type="button"
                  onClick={(e) => toggleSubMenu(link, e)}
                  className={`w-full group flex items-center ${
                    isMinimized ? 'justify-center' : 'justify-between'
                  } px-4 py-4 rounded-xl text-left transition-all duration-300 ${
                    isCurrentActive
                      ? 'bg-gradient-to-r from-blue-500/20 to-purple-500/20 text-white shadow-lg border border-blue-400/30'
                      : 'text-gray-300 hover:bg-gradient-to-r hover:from-blue-500/10 hover:to-purple-500/10 hover:text-white hover:shadow-md'
                  } backdrop-blur-sm`}
                  title={isMinimized ? link.text : undefined}
                >
                  <div className="flex items-center space-x-4">
                    <div className={`p-2 rounded-lg transition-all duration-300 ${
                      isCurrentActive
                        ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-lg'
                        : 'bg-gray-700/50 text-gray-400 group-hover:bg-gradient-to-r group-hover:from-blue-500/50 group-hover:to-purple-500/50 group-hover:text-white'
                    }`}
                    >
                      <FontAwesomeIcon icon={link.icon} className="text-lg" />
                    </div>
                    {!isMinimized && (
                      <div className="flex-1">
                        <span className="font-semibold text-sm">{link.text}</span>
                        <p className="text-xs text-gray-400 mt-1">{link.description}</p>
                      </div>
                    )}
                  </div>
                  {!isMinimized && (
                    <FontAwesomeIcon
                      icon={isSubmenuOpen ? faChevronDown : faChevronRight}
                      className={`text-sm transition-transform duration-300 ${
                        isCurrentActive ? 'text-blue-300' : 'text-gray-500 group-hover:text-blue-300'
                      } ${isSubmenuOpen ? 'rotate-180' : ''}`}
                    />
                  )}
                </button>
              ) : (
                <Link
                  to={link.to}
                  className={`group flex items-center ${
                    isMinimized ? 'justify-center' : ''
                  } space-x-4 px-4 py-4 rounded-xl transition-all duration-300 ${
                    isCurrentActive
                      ? 'bg-gradient-to-r from-blue-500/20 to-purple-500/20 text-white shadow-lg border border-blue-400/30'
                      : 'text-gray-300 hover:bg-gradient-to-r hover:from-blue-500/10 hover:to-purple-500/10 hover:text-white hover:shadow-md'
                  } backdrop-blur-sm`}
                  title={isMinimized ? link.text : undefined}
                >
                  <div className={`p-2 rounded-lg transition-all duration-300 ${
                    isCurrentActive
                      ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-lg'
                      : 'bg-gray-700/50 text-gray-400 group-hover:bg-gradient-to-r group-hover:from-blue-500/50 group-hover:to-purple-500/50 group-hover:text-white'
                  }`}
                  >
                    <FontAwesomeIcon icon={link.icon} className="text-lg" />
                  </div>
                  {!isMinimized && (
                    <div className="flex-1">
                      <span className="font-semibold text-sm">{link.text}</span>
                      <p className="text-xs text-gray-400 mt-1">{link.description}</p>
                    </div>
                  )}
                </Link>
              )}

              {/* Submenu */}
              {hasSubmenu && isSubmenuOpen && !isMinimized && (
                <div className="ml-8 space-y-2 animate-fadeIn bg-gray-800/50 rounded-xl p-3 backdrop-blur-sm border border-gray-700/50">
                  {link.submenuItems.map((subMenuItem) => (
                    <Link
                      key={subMenuItem.to}
                      to={subMenuItem.to}
                      className={`block px-4 py-3 rounded-lg text-sm transition-all duration-300 ${
                        isActive(subMenuItem.to)
                          ? 'bg-gradient-to-r from-blue-500/20 to-purple-500/20 text-blue-300 font-medium border border-blue-400/30'
                          : 'text-gray-400 hover:bg-gradient-to-r hover:from-blue-500/10 hover:to-purple-500/10 hover:text-white'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span>{subMenuItem.text}</span>
                        <span className="text-xs text-gray-500">{subMenuItem.description}</span>
                      </div>
                    </Link>
                  ))}
                </div>
              )}

              {/* Floating Popover for Minimized Sidebar */}
              {hasSubmenu && isSubmenuOpen && isMinimized && (
                <div
                  className="fixed z-50 bg-gray-800/95 backdrop-blur-md rounded-xl shadow-2xl p-3 mt-2 animate-fadeIn border border-gray-700/50"
                  style={{
                    top: popoverPosition.top,
                    left: popoverPosition.left + 8,
                    minWidth: 220,
                  }}
                  onMouseLeave={() => {
                    setShowSubMenu(null);
                  }}
                >
                  <div className="space-y-2">
                    {link.submenuItems.map((subMenuItem) => (
                      <Link
                        key={subMenuItem.to}
                        to={subMenuItem.to}
                        className={`block px-4 py-3 rounded-lg text-sm transition-all duration-300 ${
                          isActive(subMenuItem.to)
                            ? 'bg-gradient-to-r from-blue-500/20 to-purple-500/20 text-blue-300 font-medium'
                            : 'text-gray-400 hover:bg-gradient-to-r hover:from-blue-500/10 hover:to-purple-500/10 hover:text-white'
                        }`}
                        onClick={() => setShowSubMenu(null)}
                      >
                        <div className="flex items-center justify-between">
                          <span>{subMenuItem.text}</span>
                          <span className="text-xs text-gray-500">{subMenuItem.description}</span>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* Footer */}
      <div className={`p-6 bg-gradient-to-r from-gray-800/50 to-gray-700/50 backdrop-blur-sm border-t border-gray-700/50 ${
        isMinimized ? 'text-center px-2' : ''
      }`}
      >
        {!isMinimized ? (
          <div className="space-y-2">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-lg">
                <FontAwesomeIcon icon={faCog} className="text-blue-300" />
              </div>
              <div>
                <p className="text-sm font-medium text-white">SMS System v1.0</p>
                <p className="text-xs text-gray-400">
                  ©
                  {currentYear}
                  {' '}
                  ANS
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex justify-center">
            <div className="p-2 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-lg">
              <FontAwesomeIcon icon={faCog} className="text-blue-300" title="Settings" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

SideMenu.propTypes = {
  isMinimized: PropTypes.bool.isRequired,
  setIsMinimized: PropTypes.func.isRequired,
  isAdmin: PropTypes.bool,
};

SideMenu.defaultProps = {
  isAdmin: false,
};

export default SideMenu;
